import createClient from 'openapi-fetch'
import type { components, paths } from './api.gen'

// Typed client generated from the Go server's OpenAPI document (`make types`).
export const api = createClient<paths>({ baseUrl: '/api', credentials: 'same-origin' })

export type Schemas = components['schemas']
export type Me = Schemas['MeBody']
export type League = Schemas['LeagueView']
export type Member = Schemas['MemberView']
export type Round = Schemas['RoundView']
export type Category = Schemas['CategoryView']
export type Submission = Schemas['SubmissionView']
export type Reaction = Schemas['ReactionView']
export type Comment = Schemas['CommentView']
export type Media = Schemas['MediaView']
export type Ballot = Schemas['BallotView']
export type Pet = Schemas['PetView']
export type Season = Schemas['SeasonView']

/** An API failure with a message fit to show a person. */
export class ApiError extends Error {
  status: number
  /** Field-level problems, keyed by field name ("username", "password"). */
  fields: Record<string, string>
  constructor(status: number, message: string, fields: Record<string, string> = {}) {
    super(message)
    this.status = status
    this.fields = fields
  }
}

type Problem = { detail?: string; errors?: { location?: string; message?: string }[] | null }

function toError(status: number, body: unknown): ApiError {
  const p = (body ?? {}) as Problem
  const fields: Record<string, string> = {}
  for (const e of p.errors ?? []) {
    const name = e.location?.replace(/^body\./, '')
    if (name && e.message) fields[name] = e.message
  }
  let message = p.detail ?? ''
  if (!message || message === 'validation failed') {
    message = status >= 500 ? 'Something went wrong on our side. Try again in a moment.' : 'Check the highlighted fields and try again.'
  }
  if (status === 0) message = "Can't reach the server. Check your connection."
  return new ApiError(status, message, fields)
}

/** Unwrap an openapi-fetch result: return the data or throw an ApiError. */
export async function call<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let res
  try {
    res = await p
  } catch {
    throw toError(0, null)
  }
  if (res.error !== undefined || !res.response.ok) throw toError(res.response.status, res.error)
  return res.data as T
}

export function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong.'
}

/** A connection that sends nothing for this long has stalled: browsers would
 *  otherwise wait minutes with the progress bar frozen. */
const UPLOAD_STALL_MS = 45_000

/** Upload a photo or video with progress. Resolves to the new media row. */
export function uploadMedia(file: File, onProgress: (fraction: number) => void): Promise<Media> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/media')
    xhr.responseType = 'json'
    let stall: ReturnType<typeof setTimeout> | undefined
    const watch = () => {
      clearTimeout(stall)
      stall = setTimeout(() => {
        xhr.abort()
        reject(new ApiError(0, 'The upload stalled. Check your connection and try again.'))
      }, UPLOAD_STALL_MS)
    }
    xhr.upload.onprogress = (e) => {
      watch()
      if (e.lengthComputable) onProgress(e.loaded / e.total)
    }
    // Once the body is sent the server's reply is quick; the stall is over.
    xhr.upload.onload = () => clearTimeout(stall)
    xhr.onloadend = () => clearTimeout(stall)
    xhr.onload = () => {
      if (xhr.status === 201) resolve(xhr.response as Media)
      else reject(toError(xhr.status, xhr.response))
    }
    xhr.onerror = () => reject(toError(0, null))
    watch()
    const body = new FormData()
    body.append('file', file)
    xhr.send(body)
  })
}

/** Wait for an upload's renditions. Resolves once it's ready, rejects with the
 *  server's reason if it can't be read (or after a minute). */
export async function waitForMedia(id: number): Promise<Media> {
  for (let i = 0; i < 80; i++) {
    const m = await call(api.GET('/media/{id}', { params: { path: { id } } }))
    if (m.status === 'ready') return m
    if (m.status === 'failed') throw new ApiError(422, m.error ?? "We couldn't read that photo.")
    await new Promise((r) => setTimeout(r, 750))
  }
  throw new ApiError(504, 'That photo is taking too long. Try again.')
}
